import { useMemo, useState } from "react";
import { useServerSdk } from "../../RootNavigator/services/ServerSdk";
import { ProjectImpactSectionDescriptor } from "./utilities";
import Divider from "@hpo/client/components/Divider";
import { ProjectImpactSectionParams } from "@hpo/client/models/Archive";
import FieldsForm from "@hpo/client/utilities/fields/FieldsForm";
import TextField from "@hpo/client/utilities/fields/TextField";
import { useSubmitCallback } from "@hpo/client/utilities/useSubmitCallback";
import MultiSelectField from "@hpo/client/utilities/fields/MultiSelectField";
import Spacer from "@hpo/client/utilities/Spacer";
import Buttons from "@hpo/client/components/Buttons";
import Button from "@hpo/client/components/Button";
import DropDownField from "@hpo/client/utilities/fields/DropDownField";
import { find } from "@hpo/client/utilities/is";
import { Tag } from "@hpo/client/models/Tag";
import ErrorToast from "@hpo/client/utilities/ErrorToast";
import T from "@hpo/client/components/text/Text";
import CheckboxField from "@hpo/client/utilities/fields/CheckboxField";

type ProjectImpactParamsProps = {
  params: Partial<ProjectImpactSectionParams>;
  onChange: (params: ProjectImpactSectionParams) => void;
  onDelete: () => void;
};

export default function ProjectImpactParams(props: ProjectImpactParamsProps) {
  const { params, onDelete, onChange } = props;
  const server = useServerSdk();
  const allPeriods = server.suspender.use(server.getPeriods_());
  const allProjects = server.suspender.use(server.getProjects());
  const allOrgs = server.suspender.use(server.getOrganizations());
  const tags = server.suspender.use(server.getTags());
  const [title, setTitle] = useState<string | null>(params.title || null);
  const [periods, setPeriods] = useState<Array<string>>(params.periods || []);
  const [tag, setTag] = useState<Tag | null>(
    params.tag ? find(tags, (t) => t.id === params.tag) : null,
  );
  const [orgs, setOrgs] = useState<Array<string>>(allOrgs.map((o) => o.id));

  const selectableProjects = useMemo(() => {
    return allProjects.filter((p) => orgs.includes(p.organization.id));
  }, [allProjects, orgs]);

  const [projects, setProjects] = useState<Array<string>>(
    params.projects || [],
  );

  const [onSubmit, submission] = useSubmitCallback(() => {
    if (title === null) throw new Error("Titre manquant");
    if (periods.length === 0) throw new Error("Périodes manquantes");
    if (tag === null) throw new Error("Etiquette manquantes");
    if (projects.length === 0) throw new Error("Projets manquants");
    onChange({ type: "project-impact", title, periods, tag: tag.id, projects });
  }, [title, periods, tag, projects]);

  return (
    <FieldsForm onSubmit={onSubmit}>
      <ErrorToast error={submission.error} />
      <T style="section">{ProjectImpactSectionDescriptor.title}</T>
      <Spacer />
      <T>{ProjectImpactSectionDescriptor.description}</T>
      <Spacer />
      <Divider />
      <Spacer />
      <TextField
        value={title}
        label="Titre de la section"
        onChange={setTitle}
      />
      <Spacer />
      <MultiSelectField
        value={periods}
        label="Périodes à afficher"
        onChange={setPeriods}
        options={allPeriods}
        keyExtractor={(o) => o}
        renderOption={(o) => ({ label: o })}
      />
      <Spacer />
      <MultiSelectField
        value={orgs}
        label="Sélectionner des bénéficiaires"
        onChange={setOrgs}
        options={allOrgs}
        keyExtractor={(o) => o.id}
        renderOption={(o) => ({
          label: o.businessName,
        })}
      />
      <Spacer />
      <CheckboxField
        value={projects}
        label="Projets à afficher"
        onChange={setProjects}
        options={selectableProjects}
        keyExtractor={(o) => o.id}
        renderOption={(o) => ({
          label: o.label,
          help: o.organization.businessName,
        })}
        help="Seuls les indicateurs des projets sélectionnés seront pris en compte."
      />
      <Spacer />
      <DropDownField
        value={tag}
        label="Etiquette"
        onChange={setTag}
        options={tags}
        keyExtractor={(t) => t.id}
        renderOption={(t) => ({ label: t.label })}
      />
      <Spacer />

      <Buttons expand>
        <Button onClick={onDelete} label="Supprimer" style="danger" />
        <Button submit label="Valider" />
      </Buttons>
    </FieldsForm>
  );
}
